/**
 * 用途：市场洞察页，展示竞品分析、热门素材、行业趋势等市场情报数据
 * 路由：/insights
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { MultiSelect } from '../components/MultiSelect';
import Pagination from '../components/Pagination';
import { confirmDialog } from '../store/useConfirmStore';
import { insightsApi, type Competitor, type CompetitorDetail, type CreativeItem, type InsightOverview } from '../api/insights';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAppStore } from '../store/useAppStore';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';

type MainTab = 'monitor' | 'creative';
type DateRange = 7 | 14 | 30 | 90;

function channelTag(channel: string): string {
  if (channel === 'google') return 'gg';
  if (channel === 'facebook') return 'fb';
  return 'tt';
}

export default function MarketInsights() {
  const isMobile = useIsMobile();
  const { adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;

  const [tab, setTab] = useState<MainTab>('monitor');
  const [dateRange, setDateRange] = useState<DateRange>(7);

  const [overview, setOverview] = useState<InsightOverview | null>(null);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [compTotal, setCompTotal] = useState(0);
  const [compPage, setCompPage] = useState(1);
  const [compSize, setCompSize] = useState(20);
  const [compSearch, setCompSearch] = useState('');
  const [compStatus, setCompStatus] = useState<'all' | 'active' | 'dormant'>('all');

  const [creatives, setCreatives] = useState<CreativeItem[]>([]);
  const [creativeTotal, setCreativeTotal] = useState(0);
  const [creativePage, setCreativePage] = useState(1);
  const [creativeSize, setCreativeSize] = useState(9);
  const [creativeComp, setCreativeComp] = useState('all');
  const [creativeChannel, setCreativeChannel] = useState('all');
  const [creativeFormat, setCreativeFormat] = useState('all');
  const [creativeSort, setCreativeSort] = useState<'recency' | 'duration' | 'brand'>('recency');
  const [creativeSearch, setCreativeSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const [detail, setDetail] = useState<CompetitorDetail | null>(null);
  const [selectedAd, setSelectedAd] = useState<CreativeItem | null>(null);

  const [newComp, setNewComp] = useState({
    name: '',
    domain: '',
    industry: '眼镜电商',
    note: '',
    channels: ['google', 'facebook', 'tiktok'] as string[],
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2500);
  };

  const fetchOverview = async () => {
    const data = await insightsApi.getOverview(dateRange);
    setOverview(data);
  };

  const fetchCompetitors = async (pageValue = compPage, sizeValue = compSize) => {
    const res = await insightsApi.listCompetitors({
      page: pageValue,
      size: sizeValue,
      search: compSearch.trim() || undefined,
      status: compStatus === 'all' ? undefined : compStatus,
    });
    setCompetitors(res.list || []);
    setCompTotal(res.total || 0);
  };

  const fetchCreatives = async (pageValue = creativePage, sizeValue = creativeSize) => {
    const res = await insightsApi.listCreatives({
      page: pageValue,
      size: sizeValue,
      competitor_code: creativeComp === 'all' ? undefined : creativeComp,
      channel: creativeChannel === 'all' ? undefined : creativeChannel,
      format: creativeFormat === 'all' ? undefined : creativeFormat,
      sort_by: creativeSort,
      search: creativeSearch.trim() || undefined,
    });
    setCreatives(res.list || []);
    setCreativeTotal(res.total || 0);
  };

  const mounted = useRef(false);

  // Main load: overview + competitors (monitor tab)
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchOverview(), fetchCompetitors(1)])
      .catch((e) => showToast(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, [dateRange]);

  // Lazy-load creatives: only when creative tab is active
  useEffect(() => {
    if (tab === 'creative') {
      void fetchCreatives(1);
    }
  }, [tab, dateRange]);

  // Competitor filters (skip initial mount)
  useEffect(() => {
    if (!mounted.current) return;
    setCompPage(1);
    void fetchCompetitors(1);
  }, [compSearch, compStatus]);

  // Creative filters (skip initial mount)
  useEffect(() => {
    if (!mounted.current) return;
    setCreativePage(1);
    void fetchCreatives(1);
  }, [creativeComp, creativeChannel, creativeFormat, creativeSort, creativeSearch]);

  // Mark mounted — must be declared AFTER filter effects
  useEffect(() => { mounted.current = true; }, []);

  const monitorMetrics = useMemo(() => {
    return {
      competitorTotal: overview?.competitor_total ?? 0,
      competitorActive: overview?.competitor_active ?? 0,
      activeAdsTotal: overview?.active_ads_total ?? 0,
      newAdsInPeriod: overview?.new_ads_in_period ?? 0,
      avgDurationDays: overview?.avg_duration_days ?? 0,
    };
  }, [overview]);

  const addCompetitor = async () => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法添加竞对');
      return;
    }
    if (!newComp.name.trim() || !newComp.domain.trim()) {
      showToast('请输入品牌名称和官网域名');
      return;
    }
    try {
      await insightsApi.createCompetitor({
        name: newComp.name.trim(),
        domain: newComp.domain.trim(),
        industry: newComp.industry,
        note: newComp.note,
        channels: newComp.channels,
      });
      setShowAddModal(false);
      setNewComp({ name: '', domain: '', industry: '眼镜电商', note: '', channels: ['google', 'facebook', 'tiktok'] });
      await Promise.all([fetchOverview(), fetchCompetitors(1)]);
      showToast('已添加竞对并开始监控');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '添加失败');
    }
  };

  const viewDetail = async (id: string) => {
    try {
      const d = await insightsApi.getCompetitorDetail(id);
      setDetail(d);
      setShowDetailModal(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '加载详情失败');
    }
  };

  const toggleCompStatus = async (item: Competitor) => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法修改竞对状态');
      return;
    }
    try {
      await insightsApi.updateCompetitor(item.id, {
        status: item.status === 'active' ? 'dormant' : 'active',
      });
      await fetchCompetitors(compPage);
      showToast('状态已更新');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '更新失败');
    }
  };

  const deleteComp = async (item: Competitor) => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法删除竞对');
      return;
    }
    if (!await confirmDialog({ title: '删除竞对', message: `确认删除竞对 ${item.name} 吗？`, confirmText: '删除', danger: true })) return;
    try {
      await insightsApi.deleteCompetitor(item.id);
      await Promise.all([fetchOverview(), fetchCompetitors(1), fetchCreatives(1)]);
      showToast('竞对已删除');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '删除失败');
    }
  };

  const openAd = async (id: string) => {
    try {
      const ad = await insightsApi.getCreative(id);
      setSelectedAd(ad);
      setShowAdModal(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '加载广告失败');
    }
  };

  const totalCreativePages = Math.max(1, Math.ceil(creativeTotal / creativeSize));

  return (
    <div className="content">
      {isMobile ? (
        <div className="toolbar-mobile">
          <div className="toolbar-mobile-row tabs-row">
            <div className="page-tabs">
              <div className={`page-tab ${tab === 'monitor' ? 'active' : ''}`} onClick={() => setTab('monitor')}>竞对监控</div>
              <div className={`page-tab ${tab === 'creative' ? 'active' : ''}`} onClick={() => setTab('creative')}>素材分析</div>
            </div>
          </div>
          <div className="toolbar-mobile-row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <MultiSelect single options={[{value:'7',label:'最近 7 天'},{value:'14',label:'最近 14 天'},{value:'30',label:'最近 30 天'},{value:'90',label:'最近 90 天'}]} value={[String(dateRange)]} onChange={(vals) => setDateRange(Number(vals[0] ?? 30) as DateRange)} style={{ flex: 1, minWidth: 0, width: '100%' }} />
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowAddModal(true)}>+ 添加竞对</button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowExportModal(true)}>导出</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="toolbar">
          <div className="page-tabs">
            <div className={`page-tab ${tab === 'monitor' ? 'active' : ''}`} onClick={() => setTab('monitor')}>竞对监控</div>
            <div className={`page-tab ${tab === 'creative' ? 'active' : ''}`} onClick={() => setTab('creative')}>素材分析</div>
          </div>
          <div className="divider-v"></div>
          <MultiSelect single options={[{value:'7',label:'最近 7 天'},{value:'14',label:'最近 14 天'},{value:'30',label:'最近 30 天'},{value:'90',label:'最近 90 天'}]} value={[String(dateRange)]} onChange={(vals) => setDateRange(Number(vals[0] ?? 30) as DateRange)} />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowAddModal(true)}>+ 添加竞对</button>
            <button className="btn btn-outline btn-sm" onClick={() => setShowExportModal(true)}>导出报告</button>
          </div>
        </div>
      )}

      <div className={`sub-view ${tab === 'monitor' ? 'active' : ''}`}>
        <div className="grid-4 mb-24">
          <div className="metric-card mc-blue"><div className="metric-label">监控竞对品牌</div><div className="metric-value">{monitorMetrics.competitorTotal}</div><div style={{ fontSize: 11, color: 'var(--green)' }}>活跃 {monitorMetrics.competitorActive} 个</div></div>
          <div className="metric-card mc-cyan"><div className="metric-label">活跃广告总数</div><div className="metric-value">{monitorMetrics.activeAdsTotal.toLocaleString()}</div><div style={{ fontSize: 11, color: 'var(--text3)' }}>跨 3 个渠道</div></div>
          <div className="metric-card mc-amber"><div className="metric-label">本周新增广告</div><div className="metric-value">{monitorMetrics.newAdsInPeriod}</div><div style={{ fontSize: 11, color: 'var(--text3)' }}>来自 Ad Library</div></div>
          <div className="metric-card mc-pink"><div className="metric-label">平均投放天数</div><div className="metric-value">{monitorMetrics.avgDurationDays}<span style={{ fontSize: 14, color: 'var(--text3)' }}> 天</span></div><div style={{ fontSize: 11, color: 'var(--text3)' }}>持续投放=效果好</div></div>
        </div>

        <div className="card mb-24">
          <div className="card-header">
            <div>
              <div className="card-title">渠道广告量分布</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>基于 Ad Library 公开数据统计</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <span className="ch-tag gg">Google</span><span className="ch-tag fb">Facebook</span><span className="ch-tag tt">TikTok</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {competitors.map((c) => {
              const t = Math.max(1, c.google_ads + c.facebook_ads + c.tiktok_ads);
              const gP = Math.round((c.google_ads / t) * 100);
              const fP = Math.round((c.facebook_ads / t) * 100);
              const tP = 100 - gP - fP;
              return (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 120, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 11, color: '#fff' }}>{c.initial}</div>
                    {c.name}
                  </div>
                  <div style={{ flex: 1, height: 24, borderRadius: 4, display: 'flex', overflow: 'hidden', background: 'var(--surface3)' }}>
                    <div style={{ width: `${gP}%`, background: 'var(--gg-color)', opacity: 0.75 }}></div>
                    <div style={{ width: `${fP}%`, background: 'var(--fb-color)', opacity: 0.75 }}></div>
                    <div style={{ width: `${tP}%`, background: 'var(--tt-color)', opacity: 0.75 }}></div>
                  </div>
                  <div style={{ width: 70, textAlign: 'right', fontSize: 12, fontWeight: 700 }}>{t} 条</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card" style={{ position: 'relative' }}>
          <div className="card-header">
            <div className="card-title">竞对品牌列表</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div className="search-box" style={{ minWidth: 220 }}>
                <input value={compSearch} onChange={(e) => setCompSearch(e.target.value)} placeholder="搜索品牌名称..." />
              </div>
              <MultiSelect single options={[{value:'all',label:'全部状态'},{value:'active',label:'活跃投放'},{value:'dormant',label:'低频投放'}]} value={[compStatus]} onChange={(vals) => setCompStatus((vals[0] ?? 'all') as 'all'|'active'|'dormant')} />
            </div>
          </div>
          <div className="table-wrap">
            <table style={{ minWidth: 800 }}>
              <thead><tr><th style={{ whiteSpace: 'nowrap' }}>品牌</th><th style={{ whiteSpace: 'nowrap' }}>状态</th><th style={{ whiteSpace: 'nowrap' }}>活跃广告数</th><th style={{ whiteSpace: 'nowrap' }}><span className="ch-tag gg" style={{ fontSize: 9, padding: '1px 5px' }}>Google</span></th><th style={{ whiteSpace: 'nowrap' }}><span className="ch-tag fb" style={{ fontSize: 9, padding: '1px 5px' }}>Facebook</span></th><th style={{ whiteSpace: 'nowrap' }}><span className="ch-tag tt" style={{ fontSize: 9, padding: '1px 5px' }}>TikTok</span></th><th style={{ whiteSpace: 'nowrap' }}>广告量趋势</th><th style={{ whiteSpace: 'nowrap' }}>主推品类</th><th style={{ whiteSpace: 'nowrap' }}>操作</th></tr></thead>
              <tbody>
                {loading && competitors.length === 0 ? (
                  <tr><td colSpan={9}><TableLoadingCell /></td></tr>
                ) : competitors.map((c) => {
                  const trendColor = c.ad_trend > 0 ? 'var(--green)' : c.ad_trend < 0 ? 'var(--red)' : 'var(--text3)';
                  return (
                    <tr key={c.id}>
                      <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><div style={{ width: 32, height: 32, borderRadius: 8, background: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, color: '#fff' }}>{c.initial}</div><div><div style={{ fontWeight: 700 }}>{c.name}</div><div style={{ fontSize: 10, color: 'var(--text3)' }}>{c.domain}</div></div></div></td>
                      <td><span className={`status ${c.status === 'active' ? 'status-active' : 'status-dormant'}`}>{c.status === 'active' ? '活跃投放' : '低频投放'}</span></td>
                      <td style={{ fontWeight: 700 }}>{c.total_ads}</td>
                      <td style={{ fontWeight: 600, color: 'var(--gg-color)' }}>{c.google_ads}</td>
                      <td style={{ fontWeight: 600, color: 'var(--fb-color)' }}>{c.facebook_ads}</td>
                      <td style={{ fontWeight: 600, color: 'var(--tt-color)' }}>{c.tiktok_ads}</td>
                      <td><span style={{ color: trendColor, fontWeight: 700 }}>{c.ad_trend > 0 ? '↑' : c.ad_trend < 0 ? '↓' : '→'} {Math.abs(c.ad_trend)}%</span></td>
                      <td style={{ fontSize: 11 }}>{(c.categories || []).slice(0, 2).join(', ')}</td>
                      <td>
                        <button className="btn btn-sm btn-outline" onClick={() => void viewDetail(c.id)}>详情</button>{' '}
                        <button className="btn btn-sm btn-ghost" onClick={() => { setTab('creative'); setCreativeComp(c.code); }}>广告</button>{' '}
                        <button className="btn btn-sm btn-ghost" onClick={() => void toggleCompStatus(c)}>{c.status === 'active' ? '设为低频' : '设为活跃'}</button>{' '}
                        <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }} onClick={() => void deleteComp(c)}>删除</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {loading && competitors.length > 0 && <TableLoadingOverlay />}
          </div>
          <Pagination
            page={compPage}
            totalPages={Math.max(1, Math.ceil(compTotal / compSize))}
            onChange={(p) => { setCompPage(p); void fetchCompetitors(p); }}
            total={`共 ${compTotal} 个竞对品牌`}
            pageSize={compSize}
            pageSizeOptions={[10, 20, 50]}
            onPageSizeChange={(nextPageSize) => { setCompSize(nextPageSize); setCompPage(1); void fetchCompetitors(1, nextPageSize); }}
            disabled={loading}
          />
        </div>
      </div>

      <div className={`sub-view ${tab === 'creative' ? 'active' : ''}`}>
        {isMobile ? (
          <div className="toolbar-mobile mb-16">
            <div className="toolbar-mobile-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 0 }}>
                <MultiSelect single options={[{value:'all',label:'全部竞对'},...competitors.map((c) => ({value:c.code,label:c.name}))]} value={[creativeComp]} onChange={(vals) => setCreativeComp(vals[0] ?? 'all')} style={{ flex: 1, minWidth: 0 }} />
                <MultiSelect single options={[{value:'all',label:'全部渠道'},{value:'google',label:'Google'},{value:'facebook',label:'Facebook'},{value:'tiktok',label:'TikTok'}]} value={[creativeChannel]} onChange={(vals) => setCreativeChannel(vals[0] ?? 'all')} style={{ flex: 1, minWidth: 0 }} />
              </div>
            </div>
            <div className="toolbar-mobile-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 0 }}>
                <MultiSelect single options={[{value:'all',label:'全部类型'},{value:'image',label:'图片'},{value:'video',label:'视频'},{value:'carousel',label:'轮播'}]} value={[creativeFormat]} onChange={(vals) => setCreativeFormat(vals[0] ?? 'all')} style={{ flex: 1, minWidth: 0 }} />
                <MultiSelect single options={[{value:'recency',label:'按最新排序'},{value:'duration',label:'按时长排序'},{value:'brand',label:'按品牌排序'}]} value={[creativeSort]} onChange={(vals) => setCreativeSort((vals[0] ?? 'recency') as 'recency'|'duration'|'brand')} style={{ flex: 1, minWidth: 0 }} />
              </div>
            </div>
            <div className="toolbar-mobile-row">
              <div className="search-box" style={{ flex: 1 }}><input placeholder="搜索广告文案..." value={creativeSearch} onChange={(e) => setCreativeSearch(e.target.value)} /></div>
            </div>
          </div>
        ) : (
          <div className="toolbar mb-16">
            <MultiSelect single options={[{value:'all',label:'全部竞对'},...competitors.map((c) => ({value:c.code,label:c.name}))]} value={[creativeComp]} onChange={(vals) => setCreativeComp(vals[0] ?? 'all')} />
            <MultiSelect single options={[{value:'all',label:'全部渠道'},{value:'google',label:'Google'},{value:'facebook',label:'Facebook'},{value:'tiktok',label:'TikTok'}]} value={[creativeChannel]} onChange={(vals) => setCreativeChannel(vals[0] ?? 'all')} />
            <MultiSelect single options={[{value:'all',label:'全部类型'},{value:'image',label:'图片'},{value:'video',label:'视频'},{value:'carousel',label:'轮播'}]} value={[creativeFormat]} onChange={(vals) => setCreativeFormat(vals[0] ?? 'all')} />
            <MultiSelect single options={[{value:'recency',label:'按最新发现排序'},{value:'duration',label:'按投放时长排序'},{value:'brand',label:'按品牌排序'}]} value={[creativeSort]} onChange={(vals) => setCreativeSort((vals[0] ?? 'recency') as 'recency'|'duration'|'brand')} />
            <div className="divider-v"></div>
            <div className="search-box" style={{ minWidth: 200 }}><input placeholder="搜索广告文案..." value={creativeSearch} onChange={(e) => setCreativeSearch(e.target.value)} /></div>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>共 {creativeTotal} 条广告</span>
          </div>
        )}

        <div className="grid-4 mb-24">
          <div className="metric-card mc-blue"><div className="metric-label">监控广告总量</div><div className="metric-value">{creativeTotal}</div><div style={{ fontSize: 11, color: 'var(--text3)' }}>覆盖 {competitors.length} 个品牌</div></div>
          <div className="metric-card mc-pink"><div className="metric-label">视频素材占比</div><div className="metric-value">{creativeTotal === 0 ? 0 : Math.round((creatives.filter((x) => x.format === 'video').length / creatives.length) * 100)}%</div><div style={{ fontSize: 11, color: 'var(--pink)' }}>视频成主流趋势</div></div>
          <div className="metric-card mc-green"><div className="metric-label">仍在投放中</div><div className="metric-value">{creatives.filter((x) => x.status === 'active').length}<span style={{ fontSize: 14, color: 'var(--text3)' }}>/ {creatives.length}</span></div><div style={{ fontSize: 11, color: 'var(--green)' }}>当前页统计</div></div>
          <div className="metric-card mc-cyan"><div className="metric-label">平均投放天数</div><div className="metric-value">{creatives.length === 0 ? 0 : Math.round(creatives.reduce((s, c) => s + c.days, 0) / creatives.length)}<span style={{ fontSize: 14, color: 'var(--text3)' }}> 天</span></div><div style={{ fontSize: 11, color: 'var(--text3)' }}>投放越久效果越稳</div></div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: isMobile ? 12 : 16 }}>
          {creatives.map((a) => {
            const comp = competitors.find((c) => c.code === a.competitor_code);
            return (
              <div key={a.id} className="ad-card" onClick={() => void openAd(a.id)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', cursor: 'pointer' }}>
                <div className="ad-preview" style={{ height: 180, background: a.preview_bg, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 40, marginBottom: 8 }}>{a.preview_icon}</div><div style={{ fontSize: 10, color: 'rgba(0,0,0,.5)' }}>{a.format === 'video' ? '视频广告' : a.format === 'carousel' ? '轮播广告' : '图片广告'}</div></div>
                  <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 4 }}><span className={`ch-tag ${channelTag(a.channel)}`} style={{ fontSize: 9, padding: '1px 6px' }}>{a.channel}</span></div>
                  <div style={{ position: 'absolute', top: 10, right: 10, padding: '2px 8px', borderRadius: 4, background: 'rgba(0,0,0,.5)', fontSize: 10, fontWeight: 700, color: a.status === 'active' ? 'var(--green)' : 'var(--text3)' }}>{a.status === 'active' ? '投放中' : '已停止'}</div>
                </div>
                <div className="ad-card-body" style={{ padding: '14px 16px' }}>
                  <div className="ad-brand" style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{comp?.name || a.competitor_code}</div>
                  <div className="ad-headline" style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{a.headline}</div>
                  <div className="ad-copy" style={{ fontSize: 11, color: 'var(--text3)' }}>{a.copy}</div>
                </div>
                <div className="ad-card-meta" style={{ display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)' }}>
                  <span>{a.days} 天</span><span>{a.cta}</span><span>{a.category}</span><span style={{ marginLeft: 'auto' }}>{a.first_seen}</span>
                </div>
              </div>
            );
          })}
        </div>
        <Pagination
          page={creativePage}
          totalPages={totalCreativePages}
          onChange={(p) => { setCreativePage(p); void fetchCreatives(p); }}
          total={`共 ${creativeTotal} 条广告`}
          pageSize={creativeSize}
          pageSizeOptions={[9, 18, 36]}
          onPageSizeChange={(nextPageSize) => { setCreativeSize(nextPageSize); setCreativePage(1); void fetchCreatives(1, nextPageSize); }}
          disabled={loading}
          style={{ marginTop: 16 }}
        />
      </div>

      {showAddModal && (
        <div className="modal-overlay open" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>添加竞对品牌</h3><button className="modal-close" onClick={() => setShowAddModal(false)}>&times;</button></div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">品牌名称</label><input className="form-input" value={newComp.name} onChange={(e) => setNewComp((p) => ({ ...p, name: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">官网域名</label><input className="form-input" value={newComp.domain} onChange={(e) => setNewComp((p) => ({ ...p, domain: e.target.value }))} /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">行业分类</label><input className="form-input" value={newComp.industry} onChange={(e) => setNewComp((p) => ({ ...p, industry: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label className="form-label">备注</label><textarea className="form-textarea" value={newComp.note} onChange={(e) => setNewComp((p) => ({ ...p, note: e.target.value }))}></textarea></div>
            </div>
            <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setShowAddModal(false)}>取消</button><button className="btn btn-primary" onClick={() => void addCompetitor()}>添加并开始监控</button></div>
          </div>
        </div>
      )}

      {showDetailModal && detail && (
        <div className="modal-overlay open" onClick={() => setShowDetailModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>{detail.name} · 竞对详情</h3><button className="modal-close" onClick={() => setShowDetailModal(false)}>&times;</button></div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
                <div className="card"><div style={{ fontSize: 22, fontWeight: 800 }}>{detail.total_ads}</div><div style={{ fontSize: 11, color: 'var(--text3)' }}>活跃广告数</div></div>
                <div className="card"><div style={{ fontSize: 22, fontWeight: 800, color: detail.ad_trend >= 0 ? 'var(--green)' : 'var(--red)' }}>{detail.ad_trend > 0 ? '+' : ''}{detail.ad_trend}%</div><div style={{ fontSize: 11, color: 'var(--text3)' }}>广告量变化</div></div>
                <div className="card"><div style={{ fontSize: 22, fontWeight: 800 }}>{detail.avg_ad_days} 天</div><div style={{ fontSize: 11, color: 'var(--text3)' }}>平均投放时长</div></div>
                <div className="card"><div style={{ fontSize: 22, fontWeight: 800 }}>{detail.video_count}</div><div style={{ fontSize: 11, color: 'var(--text3)' }}>视频素材数</div></div>
              </div>
              {detail.top_ad && (
                <div className="card">
                  <div className="card-title" style={{ marginBottom: 8 }}>最长投放广告</div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{detail.top_ad.headline}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>{detail.top_ad.copy}</div>
                </div>
              )}
            </div>
            <div className="modal-footer"><button className="btn btn-outline" onClick={() => { setShowDetailModal(false); setTab('creative'); setCreativeComp(detail.code); }}>查看该品牌全部广告</button><button className="btn btn-ghost" onClick={() => setShowDetailModal(false)}>关闭</button></div>
          </div>
        </div>
      )}

      {showAdModal && selectedAd && (
        <div className="modal-overlay open" onClick={() => setShowAdModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>广告详情</h3><button className="modal-close" onClick={() => setShowAdModal(false)}>&times;</button></div>
            <div className="modal-body">
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>{selectedAd.headline}</div>
              <div style={{ color: 'var(--text2)', marginBottom: 10 }}>{selectedAd.copy}</div>
              <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text3)' }}>
                <span>渠道: {selectedAd.channel}</span>
                <span>类型: {selectedAd.format}</span>
                <span>时长: {selectedAd.days} 天</span>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-outline btn-sm" onClick={() => { void navigator.clipboard.writeText(`${selectedAd.headline}\n${selectedAd.copy}`); showToast('广告信息已复制'); }}>复制广告信息</button><button className="btn btn-ghost" onClick={() => setShowAdModal(false)}>关闭</button></div>
          </div>
        </div>
      )}

      {showExportModal && (
        <div className="modal-overlay open" onClick={() => setShowExportModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>导出竞对分析报告</h3><button className="modal-close" onClick={() => setShowExportModal(false)}>&times;</button></div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">导出格式</label><MultiSelect
                  single
                  options={[
                    { value: 'PDF', label: 'PDF' },
                    { value: 'Excel', label: 'Excel' },
                    { value: 'PPT', label: 'PPT' },
                  ]}
                  value={[]}
                  onChange={() => {}}
                /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setShowExportModal(false)}>取消</button><button className="btn btn-primary" onClick={() => { insightsApi.exportReport().then(() => showToast('正在生成报告，完成后通知')).catch(() => showToast('导出失败')); setShowExportModal(false); }}>生成报告</button></div>
          </div>
        </div>
      )}

      {toast && <div className="toast">✅ {toast}</div>}
    </div>
  );
}
